import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Scroll } from '@silk-hq/components';
import { Link } from '@tanstack/react-router';
import { cn } from '../../lib/classNameHelpers';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../Accordion/Accordion';
import { Badge } from '../catalyst-ui-kit/badge';
import { Button } from '../catalyst-ui-kit/button';
import { Divider } from '../catalyst-ui-kit/divider';
import { NavbarItem } from '../catalyst-ui-kit/navbar';
import { Footer } from '../Footer/Footer';
import {
  SidebarBackdrop,
  SidebarContent,
  SidebarPortal,
  SidebarRoot,
  SidebarTrigger,
  SidebarView,
} from '../Sidebar/Sidebar';
import { NavLink } from './Header';

type HamburgerMenuItemProps = {
  link: NavLink;
};

const HamburgerMenuItem = ({ link }: HamburgerMenuItemProps) => {
  const { to, params, label, disabled } = link;

  return (
    <SidebarTrigger action="dismiss" asChild>
      <NavbarItem
        to={to}
        params={params}
        disabled={disabled}
        linkClassName="text-base font-normal !text-contentTertiary"
        className={cn(
          'w-full justify-start rounded-md py-2 text-left',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="flex items-center gap-2">
          {label}
          {disabled && <Badge className="text-3xs">Coming Soon</Badge>}
        </div>
      </NavbarItem>
    </SidebarTrigger>
  );
};

const HamburgerAccordion = ({ link }: HamburgerMenuItemProps) => {
  const { to, label, childRoutes } = link;

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value={to} className="bg-backgroundPrimary">
        <AccordionTrigger className="px-2 text-base font-normal text-contentTertiary">
          {label}
        </AccordionTrigger>
        <AccordionContent className="p-2">
          <div className="flex flex-col gap-y-4">
            {childRoutes?.map(
              ({
                to: childTo,
                params: childParams,
                label: childLabel,
                disabled: childDisabled,
              }) => (
                <SidebarTrigger key={childTo} action="dismiss" asChild>
                  <NavbarItem
                    to={childTo}
                    params={childParams}
                    disabled={childDisabled}
                    linkClassName="!px-0 text-sm font-normal !text-contentSecondary"
                    className={cn('w-full', childDisabled && 'cursor-not-allowed opacity-50')}
                  >
                    <div className="flex items-center gap-2">
                      {childLabel}
                      {childDisabled && <Badge className="text-3xs">Coming Soon</Badge>}
                    </div>
                  </NavbarItem>
                </SidebarTrigger>
              )
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

type HamburgerMenuProps = {
  links: NavLink[];
};

export function HamburgerMenu({ links }: HamburgerMenuProps) {
  return (
    <SidebarRoot>
      <SidebarTrigger asChild>
        <Button
          text
          color="secondary"
          aria-label="Open menu"
          className="flex items-center justify-center"
        >
          <Bars3Icon className="!size-5" />
        </Button>
      </SidebarTrigger>
      <SidebarPortal>
        <SidebarView>
          <SidebarBackdrop />
          <SidebarContent className="flex h-full flex-col">
            <div className="flex items-center justify-between px-4 py-2">
              <SidebarTrigger action="dismiss" asChild>
                <Link to="/">
                  <img src="/logo.svg" alt="Ethereal logo" className="h-5 w-auto" />
                </Link>
              </SidebarTrigger>
              <SidebarTrigger action="dismiss" asChild>
                <Button text color="secondary" aria-label="Close menu">
                  <XMarkIcon className="!size-6" />
                </Button>
              </SidebarTrigger>
            </div>
            <Scroll.Root asChild>
              <Scroll.View className="flex-1">
                <Scroll.Content className="flex flex-col">
                  <div className="flex flex-col">
                    {links.map((link) => {
                      if (!link.childRoutes || link.disabled) {
                        return <HamburgerMenuItem key={link.to} link={link} />;
                      }
                      return <HamburgerAccordion key={link.to} link={link} />;
                    })}
                  </div>
                  <Divider className="mb-2.5 mt-2" />
                  <Footer />
                </Scroll.Content>
              </Scroll.View>
            </Scroll.Root>
          </SidebarContent>
        </SidebarView>
      </SidebarPortal>
    </SidebarRoot>
  );
}
